import * as Marzipano from 'marzipano';
import type { LevelConfig, SceneConfig } from '../data/types';
import { tileFormatForScene, withTileFormat } from './tileAssets';

/** Marzipano demo tiles used until real panorama assets are exported */
const DEMO_TILE_PREFIX = 'https://www.marzipano.net/media/equirect';

const DEMO_LEVELS = [
  { tileSize: 256, size: 256, fallbackOnly: true },
  { tileSize: 512, size: 512 },
  { tileSize: 512, size: 1024 },
  { tileSize: 512, size: 2048 },
];

/** Max vertical FOV (radians) — larger value allows zooming out further */
const MAX_VFOV = (100 * Math.PI) / 180;
/** Max horizontal FOV (radians) */
const MAX_HFOV = (120 * Math.PI) / 180;
/** Default/narrow FOV — lower value = less edge distortion when panning */
export const DEFAULT_FOV = (75 * Math.PI) / 180;

function getMaxFaceResolution(levels: LevelConfig[]): number {
  return Math.max(...levels.map((level) => level.size));
}

function normalizeInitialView(view: SceneConfig['initialView']) {
  return {
    yaw: view.yaw,
    pitch: view.pitch,
    // Cap wide Marzipano exports (often 90°) to a more natural viewing angle
    fov: Math.min(view.fov ?? DEFAULT_FOV, DEFAULT_FOV),
  };
}

export function getSceneTileConfig(scene: SceneConfig) {
  if (!scene.useDemoTiles) {
    const tilesPath =
      scene.tilesPath ||
      (scene.exportFolder && scene.marzipanoSceneId
        ? `/panoramas/${scene.exportFolder}/tiles/${scene.marzipanoSceneId}/{z}/{f}/{y}/{x}.jpg`
        : `/panoramas/${scene.id}/tiles/{z}/{f}/{y}/{x}.jpg`);

    const previewPath =
      scene.previewPath ||
      (scene.exportFolder && scene.marzipanoSceneId
        ? `/panoramas/${scene.exportFolder}/tiles/${scene.marzipanoSceneId}/preview.jpg`
        : `/panoramas/${scene.id}/preview.jpg`);

    const format = tileFormatForScene(scene.id);
    return {
      tilesPath: withTileFormat(tilesPath, format),
      previewPath: withTileFormat(previewPath, format),
      levels: scene.levels,
    };
  }

  return {
    tilesPath: `${DEMO_TILE_PREFIX}/tiles/{z}/{f}/{y}/{x}.jpg`,
    previewPath: `${DEMO_TILE_PREFIX}/preview.jpg`,
    levels: DEMO_LEVELS,
  };
}

export function createMarzipanoScene(viewer: Marzipano.Viewer, scene: SceneConfig) {
  const tileConfig = getSceneTileConfig(scene);
  const geometry = new Marzipano.CubeGeometry(tileConfig.levels);
  const source = Marzipano.ImageUrlSource.fromString(tileConfig.tilesPath, {
    cubeMapPreviewUrl: tileConfig.previewPath,
  });

  const limiter = Marzipano.RectilinearView.limit.traditional(
    getMaxFaceResolution(tileConfig.levels),
    MAX_VFOV,
    MAX_HFOV
  );
  const view = new Marzipano.RectilinearView(normalizeInitialView(scene.initialView), limiter);

  return viewer.createScene({
    source,
    geometry,
    view,
    pinFirstLevel: true,
  });
}
